import * as errors from '@attain-apps/errors'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import { format } from 'pretty-format'

import * as common from '../../common'
import * as data from '../data'

import { Attribute } from './attribute'
import { Dimension } from './dimension'
import { Entity } from './entity'
import { Enum } from './enum'
import { ForRecord } from './for-record'
import type { Id } from './id'
import type { Identifiable } from './identifiable'
import type { Model } from './model'
import type { Of } from './of'
import { Purpose } from './purpose'
import { Snapshot } from './snapshot'
import { Structure } from './structure'
import { Type } from './type'

// TODO: this file is getting large enough, maybe break into smaller files (e.g.
// have a ModelManager so that we can standardize: schema.dimensions.all,
// schema.dimensions.get, schema.dimensions.has)

// TODO: Missing a lot of JSDoc comments
export class Schema {
  public get dimensions(): readonly Dimension[] {
    return this.getModelsOfType(Type.Dimension)
  }
  public get entities(): readonly Entity[] {
    return this.getModelsOfType(Type.Entity)
  }
  public get attributes(): readonly Attribute[] {
    return this.getModelsOfType(Type.Attribute)
  }
  public get snapshots(): readonly Snapshot[] {
    return this.getModelsOfType(Type.Snapshot)
  }
  public get enums(): readonly Enum[] {
    return this.getModelsOfType(Type.Enum)
  }
  public get structures(): readonly Structure[] {
    return this.getModelsOfType(Type.Structure)
  }
  public get purposes(): readonly Purpose[] {
    return this.getModelsOfType(Type.Purpose)
  }
  public get models(): readonly Model[] {
    return Array.from(this._models.values())
  }
  public get areModelsInitialized(): boolean {
    return this.models.length > 0
  }

  /**
   * Returns all {@link Attribute} that are not scoped to a particular model,
   * but rather an entire {@link ForRecord.Type}
   *
   * @see {@link getUniversalAttributesFor}
   */
  public get allUniversalAttributes(): readonly Attribute[] {
    return ForRecord.Type.ALL.flatMap(type =>
      this.getUniversalAttributesFor(type),
    )
  }

  private readonly _models = new Map<Id, Model>()

  constructor({ models = [] }: Schema.constructor.Options = {}) {
    for (const model of models) {
      this.addModel(model)
    }
  }

  public addModel(...models: Model[]): void {
    for (const model of models) {
      this._models.set(model.id, model)
      // Adds all models contained within this model
      if (Dimension.shouldBeDimension(model)) {
        const {
          attributes = [],
          entities = [],
          snapshots = [],
        } = model as Dimension
        this.addModel(...attributes, ...entities, ...snapshots)
      } else if (
        Entity.shouldBeEntity(model) ||
        Snapshot.shouldBeSnapshot(model)
      ) {
        const { attributes = [] } = model as Entity | Snapshot
        this.addModel(...attributes)
      }
    }
  }
  public removeModel(id: Id): void {
    this._models.delete(id)
  }

  public getModelsOfType<TTypes extends readonly Type[]>(
    ...types: TTypes
  ): readonly Of<TTypes[number]>[] {
    const typesSet = new Set(types)
    return this.models.filter((model: Model): model is Of<TTypes[number]> =>
      typesSet.has(Type.from(model)),
    )
  }

  public hasModel(id: Id): boolean {
    return this._models.has(id)
  }

  public getModel<TType extends Type>(id: Id<TType>): Of<TType>
  public getModel(id: Id): Model
  public getModel(id: Id): Model {
    const model = this.tryGetModel(id)
    if (!model) {
      throw new errors.NotFoundError({
        id,
        location: 'available schema',
        type: 'model',
      })
    }
    return model
  }

  public tryGetModel<TType extends Type>(id: Id<TType>): Of<TType> | undefined
  public tryGetModel(id: Id): Model | undefined
  public tryGetModel(id: Id): Model | undefined {
    return this._models.get(id)
  }

  public hasDimension = (identifiable: Dimension.Identifiable): boolean =>
    this.hasModel(Dimension.Id.from(identifiable))
  public getDimension = (identifiable: Dimension.Identifiable): Dimension =>
    this.getModel(Dimension.Id.from(identifiable))
  public tryGetDimension = (
    identifiable: Dimension.Identifiable,
  ): Dimension | undefined => this.tryGetModel(Dimension.Id.from(identifiable))

  public hasEntity = (identifiable: Entity.Identifiable): boolean =>
    this.hasModel(Entity.Id.from(identifiable))
  public getEntity = (identifiable: Entity.Identifiable): Entity =>
    this.getModel(Entity.Id.from(identifiable))
  public tryGetEntity = (
    identifiable: Entity.Identifiable,
  ): Entity | undefined => this.tryGetModel(Entity.Id.from(identifiable))

  public hasAttribute = (identifiable: Attribute.Identifiable): boolean =>
    this.hasModel(Attribute.Id.from(identifiable))
  public getAttribute = (identifiable: Attribute.Identifiable): Attribute =>
    this.getModel(Attribute.Id.from(identifiable))
  public tryGetAttribute = (
    identifiable: Attribute.Identifiable,
  ): Attribute | undefined => this.tryGetModel(Attribute.Id.from(identifiable))

  public hasSnapshot = (identifiable: Snapshot.Identifiable): boolean =>
    this.hasModel(Snapshot.Id.from(identifiable))
  public getSnapshot = (identifiable: Snapshot.Identifiable): Snapshot =>
    this.getModel(Snapshot.Id.from(identifiable))
  public tryGetSnapshot = (
    identifiable: Snapshot.Identifiable,
  ): Snapshot | undefined => this.tryGetModel(Snapshot.Id.from(identifiable))

  public hasEnum = (identifiable: Enum.Identifiable): boolean =>
    this.hasModel(Enum.Id.from(identifiable))
  public getEnum = (identifiable: Enum.Identifiable): Enum =>
    this.getModel(Enum.Id.from(identifiable))
  public tryGetEnum = (identifiable: Enum.Identifiable): Enum | undefined =>
    this.tryGetModel(Enum.Id.from(identifiable))

  public hasStructure = (identifiable: Structure.Identifiable): boolean =>
    this.hasModel(Structure.Id.from(identifiable))
  public getStructure = (identifiable: Structure.Identifiable): Structure =>
    this.getModel(Structure.Id.from(identifiable))
  public tryGetStructure = (
    identifiable: Structure.Identifiable,
  ): Structure | undefined => this.tryGetModel(Structure.Id.from(identifiable))

  public hasPurpose = (identifiable: Purpose.Identifiable): boolean =>
    this.hasModel(Purpose.Id.from(identifiable))
  public getPurpose = (identifiable: Purpose.Identifiable): Purpose =>
    this.getModel(Purpose.Id.from(identifiable))
  public tryGetPurpose = (
    identifiable: Purpose.Identifiable,
  ): Purpose | undefined => this.tryGetModel(Purpose.Id.from(identifiable))

  public getCommonEnum = ({
    identifiable,
    isMultiselect,
  }: {
    identifiable: Enum.Identifiable
    isMultiselect?: boolean
  }) =>
    Enum.toCommonEnum({
      enum: this.getEnum(identifiable),
      isMultiselect,
    })

  /**
   * Returns the {@link Attribute Attributes} of a given
   * {@link Attribute.Id.Context.Source}
   *
   * __WARNING:__ this can violate the Liskov Substitution Principle in certain
   * cases, such as:
   * ```ts
   * function getDimensionAttributesFrom(dimension: _models.Dimension.Identifiable): _models.Attribute[] {
   *   return schema.getAttributesOf(dimension)
   * }
   *
   * // Entity identifiables are valid dimension identifiables
   * const dimensionIdentifiable: _models.Dimension.Identifiable = currentEntity
   * // Uh oh! These are the attributes of the entity, not the dimension
   * const attributesOnDimension = getDimensionAttributesFrom(dimensionIdentifiable)
   * ```
   *
   * For this reason, it is better to use {@link getAttributesOfDimension},
   * {@link getAttributesOfEntity}, {@link getAttributesOfSnapshot}, or
   * {@link getAttributesOfRecordModel}
   *
   * @see {@link Attribute.isOn}
   */
  public getAttributesOf(source: Attribute.Id.Context.Source): Attribute[] {
    return this.attributes.filter(attribute =>
      Attribute.isOn(attribute, source),
    )
  }
  /**
   * Same as {@link getAttributesOf}, but avoids any ambiguity on the intended
   * target
   *
   * Useful for avoiding Liskov substitution issues
   */
  public getAttributesOfEntity(identifiable: Entity.Identifiable): Attribute[] {
    return this.getAttributesOf(Entity.Id.from(identifiable))
  }
  /**
   * Same as {@link getAttributesOf}, but avoids any ambiguity on the intended
   * target
   *
   * Useful for avoiding Liskov substitution issues
   */
  public getAttributesOfSnapshot(
    identifiable: Snapshot.Identifiable,
  ): Attribute[] {
    return this.getAttributesOf(Snapshot.Id.from(identifiable))
  }
  /**
   * Same as {@link getAttributesOf}, but avoids _most_ ambiguity on the
   * intended target
   *
   * Useful for avoiding Liskov substitution issues
   */
  public getAttributesOfRecordModel(
    identifiable: ForRecord.Identifiable,
  ): Attribute[] {
    return Snapshot.Identifiable.isIdentifiable(identifiable)
      ? this.getAttributesOfSnapshot(identifiable)
      : this.getAttributesOfEntity(identifiable)
  }
  /**
   * Same as {@link getAttributesOf}, but avoids any ambiguity on the intended
   * target
   *
   * Useful for avoiding Liskov substitution issues
   */
  public getAttributesOfDimension(
    identifiable: Dimension.Identifiable,
  ): Attribute[] {
    return this.getAttributesOf(Dimension.Id.from(identifiable))
  }
  /**
   * Same as {@link getAttributesOf}, but with a more readable name for the
   * relevant use cases
   */
  public getUniversalAttributesFor(type: ForRecord.Type): Attribute[] {
    return this.getAttributesOf(type)
  }

  /**
   * Returns all computed {@link Attribute} that match the given
   * {@link Attribute.Computation.Selector}
   */
  public getComputedAttributes(
    selector: Attribute.Computation.Selector = 'all',
  ): Required.Semi<Attribute, 'computation'>[] {
    let candidates: readonly Attribute[]
    if (selector === 'all') {
      candidates = this.attributes
    } else {
      const { attribute, attributes, using } = selector
      if (attribute || attributes) {
        // Retrieves the specified attributes
        candidates = (attributes ?? [attribute])
          .map(_attribute => this.tryGetAttribute(_attribute))
          .filter(TypeChecker.isNotNullish.raw)
      } else if (using) {
        // TODO: this should only be the attributes that are on or utilize the
        // specified models. Currently, it is all computed attributes
        candidates = this.attributes
      } else {
        throw new errors.ValidationError({
          type: 'computed attributes selector',
          value: selector,
        })
      }
    }
    return candidates.filter(
      (attribute): attribute is Required.Semi<Attribute, 'computation'> =>
        !!attribute.computation,
    )
  }

  /**
   * Returns all {@link Snapshot} in this {@link Schema} that are associated with
   * the given {@link Dimension}
   */
  public getSnapshotsForDimension(
    dimension: Dimension.Identifiable,
  ): Snapshot[] {
    const dimensionId = Dimension.Id.from(dimension)
    return this.snapshots.filter(
      snapshot => Dimension.Id.from(snapshot) === dimensionId,
    )
  }

  /**
   * Returns all {@link Entity} in this {@link Schema} that are in the given
   * {@link Dimension}, sorted by level (root {@link Entity} first)
   */
  public getEntitiesInDimension(dimension: Dimension.Identifiable): Entity[] {
    const dimensionId = Dimension.Id.from(dimension)
    return this.entities
      .filter(entity => Dimension.Id.from(entity) === dimensionId)
      .sort((a, b) => a.level - b.level)
  }
  public getEntityAtLevel(
    dimension: Dimension.Identifiable,
    level: number,
  ): Entity {
    const entity = this.tryGetEntityAtLevel(dimension, level)
    if (!entity) {
      throw new errors.NotFoundError({
        location: 'available schema',
        type: `entity at level ${level} in ${Dimension.Id.from(dimension)}`,
      })
    }
    return entity
  }
  public tryGetEntityAtLevel = (
    dimension: Dimension.Identifiable,
    level: number,
  ): Entity | undefined =>
    this.getEntitiesInDimension(dimension).find(ent => ent.level === level)

  public getRelative(
    entity: Entity.Identifiable,
    relation: data.query.Relation.Where.Singleton,
  ): Entity {
    return this.getRelatives(entity, relation)[0]
  }
  public tryGetRelative(
    entity: Entity.Identifiable,
    relation: data.query.Relation.Where.Singleton,
  ): Entity | undefined {
    return this.tryGetRelatives(entity, relation)?.[0]
  }
  public getRelatives(
    entity: Entity.Identifiable,
    relation: data.query.Relation,
  ): Entity[] {
    const relatedEntity = this.tryGetRelatives(entity, relation)
    if (!relatedEntity) {
      throw new errors.NotFoundError({
        location: 'available schema',
        type: `${relation} entity of ${Entity.Id.from(entity)}`,
      })
    }
    return relatedEntity
  }
  public tryGetRelatives(
    entity: Entity.Identifiable,
    relation: data.query.Relation,
  ): Entity[] | undefined {
    switch (relation) {
      case 'parent': {
        const relative = this.tryGetParentEntityOf(entity)
        return relative && [relative]
      }
      case 'child': {
        const relative = this.tryGetChildEntityOf(entity)
        return relative && [relative]
      }
      case 'root': {
        const relative = this.tryGetRootEntityOf(entity)
        return relative && [relative]
      }
      case 'leaf': {
        const relative = this.tryGetLeafEntityOf(entity)
        return relative && [relative]
      }
      case 'ancestors':
        return this.getAncestorsOf(entity)
      case 'descendants':
        return this.getDescendantsOf(entity)
      default:
        TypeAsserter.assertIsNever(relation)
        return
    }
  }

  public isRoot = (entity: Entity.Identifiable): boolean =>
    this.getEntity(entity).level === 0
  public getRootEntityOf = (dimension: Dimension.Identifiable): Entity =>
    this.getEntityAtLevel(dimension, 0)
  public tryGetRootEntityOf = (
    dimension: Dimension.Identifiable,
  ): Entity | undefined => this.tryGetEntityAtLevel(dimension, 0)

  /**
   * __NOTE:__ this only checks if the entity is the leaf entity within the
   * current {@link Schema}. If this {@link Schema} does not contain all
   * relevant models, it may produce an inaccurate result
   */
  public isLeaf = (entity: Entity.Identifiable): boolean =>
    Entity.Id.from(entity) === this.getLeafEntityOf(entity).id
  public getLeafEntityOf = (dimension: Dimension.Identifiable): Entity => {
    const entity = this.tryGetLeafEntityOf(dimension)
    if (!entity) {
      throw new errors.NotFoundError({
        location: 'available schema',
        type: `leaf entity for ${Dimension.Id.from(dimension)}`,
      })
    }
    return entity
  }
  public tryGetLeafEntityOf = (
    dimension: Dimension.Identifiable,
  ): Entity | undefined => {
    const entities = this.getEntitiesInDimension(dimension)
    return entities[entities.length - 1]
  }
  public getParentEntityOf(child: Entity.Identifiable): Entity {
    const childEntity = this.getEntity(child)
    return this.getEntityAtLevel(
      Dimension.Id.from(childEntity),
      childEntity.level - 1,
    )
  }
  public tryGetParentEntityOf(child: Entity.Identifiable): Entity | undefined {
    const childEntity = this.tryGetEntity(child)
    return (
      childEntity &&
      this.tryGetEntityAtLevel(
        Dimension.Id.from(childEntity),
        childEntity.level - 1,
      )
    )
  }
  public hasParentEntityOf(child: Entity.Identifiable): boolean {
    return !!this.tryGetParentEntityOf(child)
  }
  public getChildEntityOf(parent: Entity.Identifiable): Entity {
    const parentEntity = this.getEntity(parent)
    return this.getEntityAtLevel(
      Dimension.Id.from(parentEntity),
      parentEntity.level + 1,
    )
  }
  public tryGetChildEntityOf(parent: Entity.Identifiable): Entity | undefined {
    const parentEntity = this.tryGetEntity(parent)
    return (
      parentEntity &&
      this.tryGetEntityAtLevel(
        Dimension.Id.from(parentEntity),
        parentEntity.level + 1,
      )
    )
  }
  public hasChildEntityOf(parent: Entity.Identifiable): boolean {
    return !!this.tryGetChildEntityOf(parent)
  }

  public getAncestorsOf(entityIdentifiable: Entity.Identifiable): Entity[] {
    const entity = this.getEntity(entityIdentifiable)
    return this.getEntitiesInDimension(Dimension.Id.from(entity)).filter(
      ent => ent.level < entity.level,
    )
  }

  /**
   * Returns the IDs of all entities that the given model depends on. This
   * includes:
   *
   * - **Ancestors** – every entity above the given entity in its dimension
   *   hierarchy (only when the model is an {@link Entity.Id})
   * - **Reference targets** – every entity that belongs to a dimension
   *   referenced by one of the model's attributes
   *
   * This is the canonical definition of DMS model dependencies used for
   * topological ordering when inserting data records (e.g. to satisfy
   * foreign-key constraints)
   *
   * @see {@link getAncestorsOf}
   */
  public getDependenciesOf(modelId: ForRecord.Id): Entity.Id[] {
    // NOTE:  Use a Set to deduplicate: multiple reference attributes may point to the
    // same dimension, and an entity's ancestors are also included. Without
    // deduplication, TopoSorter would receive redundant dependency labels,
    // which is harmless but wasteful.
    const seen = new Set<Entity.Id>()

    if (Entity.Id.isId(modelId)) {
      for (const ancestor of this.getAncestorsOf(modelId)) {
        seen.add(ancestor.id)
      }
    }

    for (const attribute of this.getAttributesOf(modelId)) {
      if (data.Value.Type.Reference.isReference(attribute.type)) {
        for (const entity of this.getEntitiesInDimension(
          attribute.type.dimensionId,
        )) {
          seen.add(entity.id)
        }
      }
    }

    return [...seen]
  }

  public getDescendantsOf(entityIdentifiable: Entity.Identifiable): Entity[] {
    const entity = this.getEntity(entityIdentifiable)
    return this.getEntitiesInDimension(Dimension.Id.from(entity)).filter(
      ent => ent.level > entity.level,
    )
  }

  public isAncestor = (
    potentialAncestor: Entity.Identifiable,
    potentialDescendant: Entity.Identifiable,
  ): boolean =>
    Dimension.Id.isSame(potentialAncestor, potentialDescendant) &&
    this.getEntity(potentialAncestor).level <
      this.getEntity(potentialDescendant).level
  public isDescendant = (
    potentialDescendant: Entity.Identifiable,
    potentialAncestor: Entity.Identifiable,
  ): boolean => this.isAncestor(potentialAncestor, potentialDescendant)
  public tryIsAncestor(
    potentialAncestor: Entity.Identifiable,
    potentialDescendant: Entity.Identifiable,
  ): boolean | undefined {
    if (!Dimension.Id.isSame(potentialAncestor, potentialDescendant)) {
      return false
    }
    const ancestor = this.tryGetEntity(potentialAncestor)
    if (ancestor === undefined) {
      return undefined
    }
    const descendant = this.tryGetEntity(potentialDescendant)
    if (descendant === undefined) {
      return undefined
    }
    return ancestor.level < descendant.level
  }
  public tryIsDescendant = (
    potentialDescendant: Entity.Identifiable,
    potentialAncestor: Entity.Identifiable,
  ): boolean | undefined =>
    this.tryIsAncestor(potentialAncestor, potentialDescendant)

  public determineAncestor<T extends Entity.Identifiable>(
    entity1: T,
    entity2: T,
  ): T {
    if (!Dimension.Id.isSame(entity1, entity2)) {
      throw new errors.ValidationError({
        reason:
          'Must be in the same dimension in order to determine which is the ancestor',
        type: 'entity ids',
        value: [Entity.Id.from(entity1), Entity.Id.from(entity2)],
      })
    }
    return this.isAncestor(entity1, entity2) ? entity1 : entity2
  }
  public determineDescendant<T extends Entity.Identifiable>(
    entity1: T,
    entity2: T,
  ): T {
    return this.determineAncestor(entity1, entity2) === entity1
      ? entity2
      : entity1
  }
  public tryDetermineAncestor<T extends Entity.Identifiable>(
    entity1: T,
    entity2: T,
  ): T | undefined {
    if (!Dimension.Id.isSame(entity1, entity2)) {
      return undefined
    }
    switch (this.tryIsAncestor(entity1, entity2)) {
      case true:
        return entity1
      case false:
        return entity2
      default:
        return undefined
    }
  }
  public tryDetermineDescendant<T extends Entity.Identifiable>(
    entity1: T,
    entity2: T,
  ): T | undefined {
    switch (this.tryDetermineAncestor(entity1, entity2)) {
      case entity1:
        return entity2
      case entity2:
        return entity1
      default:
        return undefined
    }
  }

  public tryGetType(
    type: data.Value.Type | undefined,
  ): common.value.types.Type | undefined {
    if (!type) {
      return undefined
    }
    const { dimensionId, enumId, isFile, isMultiselect, structureId } =
      type as FlattenUnion<typeof type>
    if (common.value.types.Intrinsic.isIntrinsic(type)) {
      return type
    }
    if (isFile) {
      // Stored as a list of file names; actual file contents live in S3
      return common.value.types.Intrinsic.List
    }
    if (dimensionId) {
      if (isMultiselect) {
        return data.Value.Reference.Multi.Default.TYPE
      }
      return data.Value.Reference.Default.TYPE
    }
    if (enumId) {
      const _enum = this.tryGetEnum(enumId)
      if (!_enum) {
        return undefined
      }
      return new common.value.types.Enum({
        entries: _enum.entries,
        isMultiselect,
      })
    }
    if (structureId) {
      const structure = this.tryGetStructure(structureId)
      if (!structure) {
        return undefined
      }
      // TODO: produce structure
      // TODO: At the moment we are returning dummy implementation so that we
      // can create zone.
      console.warn('TODO: produce structure')
      return common.value.types.Structure.object({ known: {} })
    }
    return undefined
  }
  public getType(type: data.Value.Type): common.value.types.Type {
    const commonType = this.tryGetType(type)
    if (!commonType) {
      throw new errors.NotFoundError(
        {
          location: 'available schema',
          type: 'information for data type',
        },
        new Error(`Failed to find information for: ${format(type)}`),
      )
    }
    return commonType
  }
  public tryGetTypeOfAttribute(
    identifiable: Attribute.Identifiable,
  ): common.value.types.Type | undefined {
    const attribute = this.tryGetAttribute(identifiable)
    return attribute && this.tryGetType(attribute.type)
  }
  public getTypeOfAttribute(
    identifiable: Attribute.Identifiable,
  ): common.value.types.Type {
    return this.getType(this.getAttribute(identifiable).type)
  }

  /**
   * Returns a list of posible attributes that can be used to connect two provided models.
   */
  public getConnectingReferenceAttributes(
    fromModelId: Identifiable,
    toModelId: Identifiable,
  ): Attribute[] {
    return [
      // We get connecting attributes from both directions because we want to make a method response not dependant on the order of parameters
      [fromModelId, toModelId],
      [toModelId, fromModelId],
    ].flatMap(([potentialReferencerId, potentialReferenceeId]) => {
      // If a referencer is a dimension (not a data record model) we don't have precisely defined behavior, thus we return an empty array of attributes.
      // This case is possible in theory, but not present in pratice
      if (!ForRecord.Id.isId(potentialReferencerId)) {
        return []
      }
      const potentialReferenceeDimensionId =
        Dimension.Id.isId(potentialReferenceeId) ||
        ForRecord.Id.isId(potentialReferenceeId)
          ? Dimension.Id.from(potentialReferenceeId)
          : undefined
      const attributes = this.getAttributesOfRecordModel(potentialReferencerId)
      const relevantReferenceAttributes = attributes.filter(
        attribute =>
          data.Value.Type.Reference.isReference(attribute.type) &&
          attribute.type.dimensionId === potentialReferenceeDimensionId,
      )
      return relevantReferenceAttributes
    })
  }
}
export namespace Schema {
  export namespace constructor {
    export interface Options {
      /** @default [] */
      models?: readonly Model[]
    }
  }
}
